import { fetchWithChatFeedback } from './chatFeedbackRequired';
import { apiCall, type ApiResponse } from './apiClient';
import { normalizeGeorgiaUnitText } from './constructionPriceGeorgiaUnit';
import { stripLegacyWageDeterminationFields, type EstimateDraft } from './constructionPriceDraft';
import { normalizeActiveCatalogMaterialComponentKeys, normalizeApproximateCostPlan, normalizeApproximateSettings, normalizeComponentConfirmationCandidates, normalizeComponentScopeReviewCandidates, type ActiveCatalogMaterialComponentKey, type ApproximateCostPlanMaterialComponent, type ApproximateCostPlanPublic, type ApproximateCostPlanSettings, type ComponentConfirmationCandidate, type ComponentScopeReviewCandidate, type SiteStaffingRoleSelection, type SupportedCostRecipe } from './approximateCostPlan';
import { USER_FACING_ERROR_MESSAGES, normalizeApiError } from '../utils/userFacingError';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export type InclusionState = 'included' | 'excluded' | 'allowance';
export type EstimateMode = 'itemized' | 'comparable_only_rom' | 'historical_comparable_rom';
export type EstimateStatus = 'draft' | 'calculated' | 'finalized';

export interface EstimateCapabilities {
  approximate_cost_plan?: { supported_recipes: SupportedCostRecipe[] };
  applicable: boolean;
  enabled: boolean;
  provider_configured?: boolean;
  calculation_available: boolean;
  export_available: boolean;
  path_policy_version: number;
  can_view?: boolean;
  can_edit?: boolean;
  can_calculate?: boolean;
  can_finalize?: boolean;
  can_export?: boolean;
  can_cancel?: boolean;
  can_retry?: boolean;
  comparable_only_rom_finalization_available?: boolean;
  export_disabled_reason?: string;
  /** Server advertises whether this flow has trusted Stage-604 prefill available. */
  price_search_prefill_available?: boolean;
  /** Server-owned availability decision for the snapshot-backed primary path. */
  cost_data_primary?: CostDataPrimaryCapability | string;
  /** Server-owned availability decision for schedule extraction only. */
  davis_bacon_schedule?: DavisBaconScheduleCapability;
}

export type DavisBaconConstructionType = 'Building' | 'Residential' | 'Highway' | 'Heavy';
export type DavisBaconScheduleCapabilityReason = 'not_configured' | 'not_applicable' | 'prerequisites_required';
export type DavisBaconScheduleStatus = 'available' | 'review_required' | 'rejected' | 'reference_found';
export type DavisBaconScheduleReviewState = 'validated' | 'approved' | 'review_required' | 'rejected' | 'not_required';

/** A capability is an availability hint, never a legal/applicability decision. */
export interface DavisBaconScheduleCapability {
  available: boolean;
  reason: DavisBaconScheduleCapabilityReason | null;
  can_view_details?: boolean;
  can_review?: boolean;
}

/** Safe, policy-curated schedule data; source artifacts and internal lineage stay server-side. */
export interface DavisBaconScheduleSummary {
  /** Safe opaque concurrency material; used only in the review request, never rendered. */
  schedule_id?: string;
  review_token?: string;
  determination_id: string;
  revision: string;
  state: string;
  county: string;
  construction_type: DavisBaconConstructionType;
  reference_date: string;
  retrieved_at: string;
  publication_date?: string;
  effective_date?: string;
  /** Curated public attribution only; source URLs/artifacts are never exposed. */
  source_name?: 'SAM.gov';
  /** Server-derived canonical SAM.gov URL for a validated reference only. */
  determination_url?: string;
  extraction_status?: 'deferred';
  status: DavisBaconScheduleStatus;
  review_state: DavisBaconScheduleReviewState;
  classification_count: number;
}

export interface DavisBaconScheduleDetailRow {
  classification: string;
  group?: string | null;
  base_wage: string;
  fringe: string;
  total_hourly: string;
  unit: 'hour';
  footnote_identifiers?: string[];
}

export interface DavisBaconScheduleDetails extends DavisBaconScheduleSummary {
  schedule_id: string;
  rows: DavisBaconScheduleDetailRow[];
}

export type DavisBaconScheduleReviewDecision = 'approve' | 'reject';

export interface DavisBaconScheduleJob {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'stale' | 'cancelled';
  phase?: string;
  created_at?: string;
  completed_at?: string;
  cancelled_at?: string;
  retryable?: boolean;
  failure_reason?: 'no_matching_determination' | 'source_unavailable' | 'lookup_failed';
  /** A fresh lookup did not yield a replacement, so the server safely reused an exact verified schedule. */
  saved_schedule_reused?: boolean;
  schedule?: DavisBaconScheduleSummary;
}

/** Safe availability hint; server selection remains authoritative at Calculate time. */
export interface CostDataPrimaryCapability {
  status: CostDataPrimaryStatus;
}

export type CostDataPrimaryStatus =
  | 'configured'
  | 'eligible'
  | 'not_configured'
  | 'rights_pending'
  | 'rights_restricted'
  | 'source_unavailable'
  | 'no_eligible_coverage';

export type CostDataDecision =
  | 'direct_cost_estimate'
  | 'partial_itemized_estimate'
  | 'insufficient_item_evidence'
  | 'award_research_only';

export type CostDataPricingKind = 'installed_work' | 'componentized' | 'unresolved' | 'manual_allowance';

export type CostDataComponentState = 'priced' | 'included_in_installed_work' | 'not_separately_reported' | 'not_available';

/**
 * Safe, server-curated provenance. The client intentionally has no snapshot
 * ID, artifact URL, policy version, hash, or provider credential field.
 */
export interface CostDataProvenance {
  source_label: string;
  effective_date?: string | null;
  geography_fit?: 'county' | 'state' | null;
  display_class?: string | null;
  freshness_status?: 'current' | 'stale' | 'not_available' | null;
  selected_observation_count?: number | null;
  observation_start_date?: string | null;
  observation_end_date?: string | null;
  snapshot_published_at?: string | null;
  time_adjustment?: CostDataTimeAdjustment | null;
}

/** A rounded public range. Internal selection inputs and formulas stay server-side. */
export interface CostDataMoneyRange {
  low: string | number;
  base: string | number;
  high: string | number;
}

/** Safe timing context for an approved index adjustment; never includes its factor or lineage. */
export interface CostDataTimeAdjustment {
  source_label: string;
  status: 'adjusted';
  observation_period: string;
  reference_period: string;
}

export interface CostDataUnresolvedItem {
  work_item_id?: string;
  label: string;
  next_action: 'add_allowance_or_exclude';
}

export interface CostDataSummary {
  decision: CostDataDecision;
  covered_item_count: number;
  included_item_count: number;
  unresolved_items: CostDataUnresolvedItem[];
  source_freshness?: 'current' | 'stale' | 'not_available';
  provenance?: CostDataProvenance[];
}

/**
 * A deliberately narrow, server-curated BTB display projection. It is a
 * national planning reference for one exact supported technology, never a
 * project total, local price, observed bid, or component breakdown.
 */
export interface BtbPlanningBaselinePublic {
  source_label: 'DOE/NREL Buildings Technology Baseline (2024)';
  evidence_type: 'National planning baseline';
  technology_label: string;
  building_segment: 'commercial' | 'residential';
  cost_basis_label: 'Installed' | 'Retail';
  value: { amount: string | number; currency: 'USD'; unit_label: string };
  value_year_label: string;
  scenario_label?: string;
  geography_label: 'National';
  attribution: string;
  limitations: [string];
}

export interface WorkItemCostData {
  pricing_kind: CostDataPricingKind;
  component_states: Partial<Record<'labor' | 'material' | 'equipment' | 'subcontract', CostDataComponentState>>;
  provenance?: CostDataProvenance | null;
  /** Server-rounded installed unit range; populated only for installed work. */
  unit_range?: CostDataMoneyRange | null;
  /** Server-rounded quantity-extended installed range; populated only for installed work. */
  total_range?: CostDataMoneyRange | null;
}

export interface EstimateWorkItem {
  id: string;
  description: string;
  classification?: string | null;
  source_refs?: string[];
  quantity?: string | number | null;
  quantity_low?: string | number | null;
  quantity_high?: string | number | null;
  unit?: string | null;
  inclusion_state: InclusionState;
  allowance_range?: { low?: string | number | null; base?: string | number | null; high?: string | number | null } | null;
  rationale?: string | null;
  user_confirmed: boolean;
  [key: string]: unknown;
}

export type { EstimateDraft, ConstructionTimingRequirement } from './constructionPriceDraft';
export { normalizeTimingRequirements } from './constructionPriceDraft';

export type PriceSearchPrefillMode = 'initial' | 'refresh' | 'replace_generated';

/**
 * Redacted, UI-safe provenance for the server-owned Stage-604 extractor.
 * The browser deliberately never receives Construction Characteristics or a
 * model response; it only receives concise metadata needed to explain a draft.
 */
export interface PriceSearchPrefill {
  /** Editable extracted facts that do not yet meet the strict saved-draft requirements. */
  partial_draft?: Partial<Omit<EstimateDraft, 'construction_type'>> & { construction_type?: string | null };
  timing_source_status?: 'complete' | 'unreadable' | 'not_available';
  roof_insulation_sow_minimum_r?: string | null;
  roof_insulation_sow_evidence_quote?: string | null;
  /** Server-issued revision used to fence a location confirmation request. */
  prefill_revision?: number;
  status?: 'current' | 'stale' | 'pending' | string;
  outcome?: string;
  warnings?: string[];
  provenance?: {
    source?: string;
    fields?: Record<string, { source?: string; status?: string }>;
  };
  location?: {
    status?: 'explicit' | 'inferred' | 'not_found' | string;
    confidence?: 'high' | 'medium' | 'low' | string | null;
    autofilled?: boolean;
    /** The owner explicitly reviewed the inferred candidate; no evidence is exposed. */
    owner_confirmed?: boolean;
    resolution?: { outcome?: string } | null;
  };
  /** Cited, unpriced component proposals emitted by the Contract Details agent. */
  material_occurrence_proposals?: Array<{
    component_key?: ApproximateCostPlanMaterialComponent['component_key'];
    proposal_id: string; component_kind: 'interior_paint' | 'interior_primer' | 'manual_water_closet' | 'electrical_outlet' | 'drywall_level4_wood' | 'softwood_lumber_2x4x16_no2';
    label: string; excerpt: string; excerpt_sha256: string; source_label: string; source_locator: string;
    quantity?: string | null; unit?: string | null; missing_facts: string[];
  }>;
  site_staffing_proposals?: Array<{
    proposal_id: string;
    role_kind: 'site_superintendent' | 'quality_control' | 'site_safety' | 'project_manager';
    duty_description: string;
    evidence_quote: string;
    source_label: string;
    source_reference: string;
    soc_code: null;
    headcount?: string | null;
    active_workdays?: string | null;
    shift_hours?: string | null;
  }>;
  /** Cited assembly types only; selection and consequential facts remain owner-confirmed. */
  assembly_occurrence_proposals?: Array<{
    proposal_id: string; assembly_key: 'wall_finish' | 'bagged_concrete_small_volume' | 'exterior_full_broadcast_coating' | 'tuff_hide_spray_surfacer' | 'three_tab_roof_shingles';
    label: string; excerpt: string; excerpt_sha256: string; source_label: string; source_locator: string;
    quantity?: string | null; unit?: string | null; missing_facts: string[];
  }>;
  /**
   * Additive general takeoff payload.  It is owner-facing evidence only: the
   * browser cannot use it to select catalog data, wages, or a calculation
   * path.  Old prefill payloads intentionally omit this field.
   */
  component_takeoff_proposals?: Array<{
    proposal_id: string;
    occurrence_id?: string;
    component_kind?: string;
    identity_provenance?: 'source_stated' | 'planning_assumption';
    identity_assumptions?: string[];
    /** Owner-facing label; display_label is accepted only during the API transition. */
    label?: string;
    display_label?: string;
    /** `ready_to_review` is canonical; `ready` is accepted from older saved prefill. */
    status?: 'ready_to_review' | 'ready' | 'needs_information' | 'ambiguous' | 'not_yet_priceable' | string;
    quantity_provenance?: 'directly_stated' | 'derived_from_cited_facts' | 'ai_planning_estimate' | 'missing' | 'ambiguous' | string;
    provenance_tier?: 'directly_stated' | 'derived_from_cited_facts' | 'ai_planning_estimate' | 'missing' | 'ambiguous' | string;
    source_label?: string;
    source_locator?: string;
    excerpt?: string;
    excerpt_sha256?: string;
    quantity?: string | null;
    unit?: string | null;
    formula?: string | null;
    formula_evidence?: string[];
    planning_range?: { low?: string | null; base?: string | null; central?: string | null; high?: string | null } | null;
    confidence?: 'high' | 'medium' | 'low' | string | null;
    rationale?: string | null;
    assumptions?: string[];
    missing_facts?: string[];
    /** Registry-validated only; the UI will still require normal owner review. */
    component_key?: string | null;
    assembly_key?: string | null;
  }>;
  [key: string]: unknown;
}

export interface PriceSearchPrefillResponse {
  draft: EstimateDraft | null;
  prefill: PriceSearchPrefill;
}

export interface SiteRoleWagePreview {
  status: 'available' | 'location_required' | 'unavailable';
  soc_code: string;
  hourly_mean?: string;
  geography_label?: string;
  geography_level?: 'metro' | 'state' | 'national';
  release_year?: number;
  source_url?: string;
  source_retrieved_at?: string;
  fallback_reason?: string | null;
  limitation?: string;
}

export const getConstructionSiteRoleWagePreview = (flowId: string, socCode: string): Promise<SiteRoleWagePreview> =>
  unwrap(apiCall<SiteRoleWagePreview>(`${root(flowId)}/site-role-wage-preview?soc_code=${encodeURIComponent(socCode)}`, 'GET'));

/** A durable, server-owned prefill job. It intentionally contains no prompt or source text. */
export interface PriceSearchPrefillJob {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'stale' | 'failed' | 'cancelled' | string;
  created_at?: string;
  mode?: PriceSearchPrefillMode;
  draft?: EstimateDraft | null;
  prefill?: PriceSearchPrefill | null;
}

/** Read-only craft hours and separately reviewed site-staffing proposal. No wages or dollars are model output. */
export interface ConstructionLaborPlanPreview {
  craft_hours: Array<{
    occurrence_id: string;
    craft: string;
    quantity: string;
    unit: string;
    hours_per_unit: string;
    person_hours: string;
    source_version: string;
    wage_state: 'verified' | 'pending_lookup' | 'unavailable' | string;
    coverage_state: string;
  }>;
  unmodeled_work: string[];
  overlap_work?: string[];
  duplicate_occurrences?: string[];
  coverage_state: string;
}

export interface ConstructionLaborPlanProposal {
  proposal_id: string;
  schema_version: 'labor-plan-proposal-v1';
  status: 'proposed' | 'applied' | 'stale' | string;
  generation_basis?: 'ai_planning_assumption' | 'rule_based_fallback' | string;
  input_hash: string;
  expected_draft_revision: number;
  expected_draft_content_hash: string;
  craft_hour_preview: ConstructionLaborPlanPreview;
  site_staffing_roles: SiteStaffingRoleSelection[];
  schedule: {
    average_concurrent_crew?: string | null;
    shift_hours?: string | null;
    parallel_work?: boolean | null;
    derived_site_days?: string | null;
    confidence?: string;
    rationale?: string;
    coverage_state: string;
    scope_label?: string | null;
  };
  field_basis?: Record<string, Record<string, string>>;
}

export interface ReviewedLaborSchedule {
  average_concurrent_crew: string;
  parallel_work: boolean;
}

export interface ConstructionLaborPlanJob {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'stale' | string;
  proposal?: ConstructionLaborPlanProposal | null;
  failure_reason?: string | null;
}

/** Safe review candidate. Opaque IDs are submitted back unchanged; source taxonomy details stay server-side. */
export interface SourceMatchReviewCandidate {
  proposal_id: string;
  review_label: string;
  scope_label?: string;
  measurement_label?: string;
}

export interface SourceMatchProposalPublic {
  proposal_revision: number;
  candidates: SourceMatchReviewCandidate[];
}

export interface SourceMatchReviewDecision {
  proposal_id: string;
  decision: 'accept' | 'decline';
}

export interface EstimateComparableReview {
  award_id: string;
  included: false;
  rationale: string;
}

export interface EstimateAdjustment {
  name?: string;
  adjustment_type?: 'percentage' | 'amount';
  calculation_base?: 'direct' | 'running_total';
  low_value?: string | number;
  base_value?: string | number;
  high_value?: string | number;
  sequence?: number;
  rationale?: string;
  [key: string]: unknown;
}

export interface EstimateDraftRequest extends Omit<EstimateDraft, 'draft_revision'> {
  expected_draft_revision: number;
  change_reason: string;
}

export interface EstimateReadiness {
  ready_to_calculate: boolean;
  blockers: Array<string | { code?: string; message?: string; work_item_id?: string }>;
}

export interface EstimateJobPhase {
  source?: string;
  label?: string;
  status: string;
  message?: string;
}

/** Public, curated state for one source lane in unified research. */
export type UnifiedSourceLaneState =
  | 'queued' | 'running' | 'reused' | 'complete'
  | 'no_matching_evidence' | 'needs_contract_details'
  | 'unavailable' | 'failed' | 'skipped' | 'cancelled';

export interface UnifiedSourceLaneProgress {
  label: string;
  state: UnifiedSourceLaneState;
  message?: string;
  evidence_count?: number;
}

/** A compact owner-facing ledger of sources actually checked for one result. */
export interface UnifiedSourceCheckedPublic {
  label: string;
  detail?: string;
  /** A server-curated owner-facing source state; never a transport diagnostic. */
  outcome?: 'consulted' | 'reused' | 'no_matching_evidence' | 'unavailable' | 'not_available' | 'not_applicable';
  contribution?: 'used' | 'context' | 'not_used';
  evidence_count?: number;
  as_of?: string;
}

export type UnifiedPriceRecommendationOutcome =
  | 'recommended' | 'evidence_conflict' | 'benchmark_only' | 'context_only' | 'insufficient';

/** A source-native public range. The browser must never calculate or reshape it. */
export interface UnifiedPlanningRangePublic {
  low: string | number;
  median: string | number;
  high: string | number;
  unit_label: string;
  basis_label: string;
  pricing_date_label: string;
}

/**
 * A distinct whole-award point selected by the server after strict cohort
 * qualification.  It deliberately carries no range endpoints, record IDs,
 * scores, source-run IDs, or internal selection rationale.
 */
export interface ConservativeComparableAwardPointPublic {
  reference_kind: 'conservative_comparable_award_point';
  amount: string | number;
  source_label: 'Previous federal awards — USAspending';
  basis_label: string;
  selection_rule: 'lowest_qualified_not_below_modeled_subtotal' | 'highest_qualified_when_all_below_modeled_subtotal';
  geography_tier: 'county_exact' | 'city_exact' | 'statewide';
  qualified_family_count: number;
  source_date_label: string;
  policy_version: string;
}

/** Deliberately compact, nonnumeric explanation behind the selected result. */
export interface UnifiedEvidenceSummaryPublic {
  primary_summary?: string;
  corroboration_summary?: string;
  context_summary?: string;
}

export interface UnifiedPriceRecommendationPublic {
  outcome: UnifiedPriceRecommendationOutcome;
  display_label: 'Recommended planning price';
  primary_source_label?: string;
  range?: UnifiedPlanningRangePublic | null;
  award_point?: ConservativeComparableAwardPointPublic | null;
  /** Server-selected presentation for an observed award cohort; never inferred by JSX. */
  display_shape?: 'single' | 'range' | 'distribution' | 'award_point';
  cohort_count?: number;
  range_quality_warning?: 'Wide price range—review before relying on it.' | null;
  basis_label?: string;
  applicability_label?: string;
  evidence_summary?: UnifiedEvidenceSummaryPublic;
  limitation: string;
}

export interface UnifiedEvidenceDrawerPublic {
  primary_summary?: string;
  corroboration_summary?: string;
  context_summary?: string;
  limitation?: string;
}

/**
 * Curated explanation of whether USAspending records formed a usable award
 * cohort.  The server performs every retrieval and selection decision; this
 * projection deliberately has no scores, query text, record IDs, or reasons.
 */
export type ComparableCohortStatus = 'tight_supported' | 'broader_supported' | 'insufficient_similarity' | 'context_only' | 'source_unavailable';
export type ComparableCohortGeographicTier = 'county' | 'city' | 'state';

export interface ComparableCohortPublic {
  status: ComparableCohortStatus;
  summary: string;
  geographic_tier?: ComparableCohortGeographicTier;
  selected_count: number;
  excluded_count?: number;
}

/**
 * BTB is national technology evidence beneath the award-derived planning
 * recommendation. These are presentation values emitted by the server; the
 * browser must never derive, scale, sum, or reconcile them.
 */
export type BtbBreakdownStatus = 'available' | 'partial' | 'no_matches' | 'unavailable';

export interface BtbTechnologyMoneyRangePublic {
  low: string | number;
  median: string | number;
  high: string | number;
  unit_label: string;
}

export interface BtbTechnologyDetailPublic {
  project_item_label: string;
  technology_label: string;
  end_use_label?: string;
  building_segment: 'Commercial' | 'Residential';
  fuel_label?: string;
  model_year: number;
  scenario_label: 'Reference';
  /** Human-facing server decision; no client-side scope inference is allowed. */
  construction_scope_label?: 'New construction' | 'Retrofit';
  /** Source-typical is not a project quantity; Project-scaled passed server formula gates. */
  value_basis_label: 'Source-typical' | 'Project-scaled';
  typical_unit_label?: string;
  retail_benchmark: BtbTechnologyMoneyRangePublic;
  installation_allowance: BtbTechnologyMoneyRangePublic;
  /** Present only after one construction scope was selected. */
  installed_total?: BtbTechnologyMoneyRangePublic;
  /** Both are present only when construction scope is still ambiguous. */
  installed_new_construction?: BtbTechnologyMoneyRangePublic;
  installed_retrofit?: BtbTechnologyMoneyRangePublic;
  range_quality_warning?: typeof RANGE_TARGET_WARNING;
  negative_installation_warning?: 'Source model produces a negative installation adjustment.';
  source_quality_summary?: string;
  attribution: string;
  limitation: string;
}

export interface BtbBreakdownPublic {
  status: BtbBreakdownStatus;
  heading: 'Technology cost details';
  source_label: 'DOE/NREL Buildings Technology Baseline (2024)';
  geography_label: 'National';
  value_year_label: '2023 dollars';
  items: BtbTechnologyDetailPublic[];
  /** Count only; source-item internals and matcher reasons stay server-side. */
  unmatched_count?: number;
}

/**
 * Curated supporting evidence from an approved Georgia public-owner artifact.
 * It is deliberately subordinate to the award planning recommendation: it is
 * an observed all-in installed line, never a synthesized project price or a
 * component-cost breakdown.
 */
export type GeorgiaObservedEvidenceStatus = 'available' | 'no_match' | 'insufficient_evidence' | 'not_configured';

export interface GeorgiaObservedEvidenceItemPublic {
  work_item: string;
  original_description: string;
  unit: string;
  quantity: string | number;
  unit_price: string | number;
  extended_price: string | number;
  currency: 'USD';
  price_date: string;
  evidence_status: 'Awarded' | 'Final paid';
  project_location: string;
  source_label: 'Approved Georgia public owner';
  source_reference: string;
  caveats: string[];
}

export interface GeorgiaObservedEvidencePublic {
  status: GeorgiaObservedEvidenceStatus;
  heading: 'Georgia public-owner price evidence';
  scope: 'Georgia public-owner observed installed-price evidence';
  as_of?: string;
  items: GeorgiaObservedEvidenceItemPublic[];
  warnings: string[];
}

/**
 * A reviewed public whole-award record that is useful as planning context.
 * This is intentionally not a price line: it cannot be assigned to Labor,
 * Materials, or another modeled ROM category in the browser.
 */
export interface PublicPriceEvidenceItemPublic {
  label: string;
  amount: string | number;
  currency: 'USD';
  evidence_tier: string;
  economic_scope: 'Whole awarded project';
  contribution: 'context_only';
  geography_label: string;
  price_date: string;
  limitation: string;
  source_label: string;
  source_url: string;
}

export interface PublicPriceEvidencePublic {
  status: 'available' | 'unavailable';
  items: PublicPriceEvidenceItemPublic[];
}

/**
 * The only unified search result shape that reaches JSX. Its normalizer drops
 * source records, taxonomy/match facts, scores, snapshot/run/policy IDs, and
 * every unselected source value before React sees it.
 */
export interface UnifiedSourceResearchPublic {
  schema_version: 'unified-source-research-v1';
  recommendation: UnifiedPriceRecommendationPublic;
  approximate_cost_plan?: ApproximateCostPlanPublic | null;
  btb_breakdown?: BtbBreakdownPublic | null;
  georgia_observed_evidence?: GeorgiaObservedEvidencePublic | null;
  /** Whole-award context only; never a component-cost input in JSX. */
  public_price_evidence?: PublicPriceEvidencePublic | null;
  comparable_cohort?: ComparableCohortPublic | null;
  evidence_drawer: UnifiedEvidenceDrawerPublic;
  lanes: UnifiedSourceLaneProgress[];
  sources_checked?: UnifiedSourceCheckedPublic[];
}

export interface EstimateJob {
  job_id: string;
  status: 'pending' | 'queued' | 'processing' | 'running' | 'blocked' | 'complete' | 'completed' | 'failed' | 'stale' | 'canceled' | 'cancelled';
  phases?: EstimateJobPhase[];
  phase?: string;
  source_phases?: Record<string, string>;
  source_statuses?: EstimateJobPhase[];
  message?: string;
  revision?: number | null;
  mode?: 'historical_award_research' | EstimateMode | string;
  source_runs?: Array<Record<string, unknown>>;
  unified_source_research?: UnifiedSourceResearchPublic | null;
  research_snapshot?: Record<string, unknown> | null;
  retryable?: boolean;
  capabilities?: { can_cancel?: boolean; can_retry?: boolean };
  error?: { category?: string; retryable?: boolean };
}

export interface EstimateRevisionSummary {
  revision: number;
  status: EstimateStatus;
  created_at?: string;
  finalized_at?: string;
}

export interface EstimateEvidence {
  source?: string;
  source_record_id?: string | null;
  source_id?: string | null;
  adapter_version?: string | null;
  effective_date?: string | null;
  retrieved_at?: string | null;
  geography?: string | null;
  geography_fit?: string | null;
  priced_unit?: string | null;
  source_unit?: string | null;
  unit_conversion?: string | null;
  transformation?: string | null;
  freshness_status?: string | null;
  display_class?: string | null;
  [key: string]: unknown;
}

export interface EstimateRevisionWorkItem extends Record<string, any> {
  evidence?: EstimateEvidence | EstimateEvidence[] | null;
  cost_data?: WorkItemCostData | null;
}

export interface EstimateRevision {
  approximate_cost_plan?: ApproximateCostPlanPublic | null;
  estimate_id?: string;
  revision: number;
  status: EstimateStatus;
  mode: EstimateMode;
  currency: 'USD' | string;
  estimate_date?: string;
  totals?: Record<string, string | number | null | undefined>;
  confidence?: { band?: string; reasons?: string[]; score?: string | number };
  assumptions?: string[];
  exclusions?: string[];
  warnings?: string[];
  work_items?: EstimateRevisionWorkItem[];
  adjustments?: Array<Record<string, any>>;
  required_acknowledgements?: string[];
  comparables?: Record<string, any>;
  source_runs?: Array<Record<string, unknown>>;
  source_statuses?: Array<Record<string, any>>;
  source_phases?: Record<string, string>;
  degradation?: { is_degraded?: boolean; reasons?: string[]; acknowledgement_required?: boolean; acknowledged_at?: string };
  /** Additive, safe primary-cost path decision. Omitted for legacy results. */
  cost_data_summary?: CostDataSummary | null;
  /** Optional, separately approved BTB national planning-baseline display only. */
  btb_planning_baseline?: BtbPlanningBaselinePublic | null;
  /** Safe result projection for the unified source-research experience. */
  unified_source_research?: UnifiedSourceResearchPublic | null;
  created_at?: string;
  finalized_at?: string;
  [key: string]: unknown;
}

export interface EstimateLoadData {
  capabilities: EstimateCapabilities;
  draft: EstimateDraft | null;
  active_revision: EstimateRevision | null;
  revision_history: EstimateRevisionSummary[];
  job: EstimateJob | null;
  readiness: EstimateReadiness;
  trusted_basis?: Record<string, unknown>;
  upstream_source_hashes?: Record<string, unknown>;
  source_statuses?: Array<Record<string, any>>;
  historical_award_research?: Record<string, unknown> | null;
  prefill?: PriceSearchPrefill | null;
  /** The saved scope has not yet been checked for cited site-staffing roles. */
  site_staffing_prefill_needed?: boolean;
  /** A running prefill is durable so an owner can safely reload and rejoin it. */
  prefill_job?: PriceSearchPrefillJob | null;
  labor_plan_proposal?: ConstructionLaborPlanProposal | null;
  labor_plan_job?: ConstructionLaborPlanJob | null;
  /** Durable schedule state is independent of estimate revisions and price jobs. */
  davis_bacon_schedule?: DavisBaconScheduleSummary | null;
  davis_bacon_schedule_job?: DavisBaconScheduleJob | null;
  /** Durable safe abstention/coverage state, including award-research-only fallback. */
  cost_data_summary?: CostDataSummary | null;
  unified_source_research?: UnifiedSourceResearchPublic | null;
  source_match_proposal?: SourceMatchProposalPublic | null;
  /** Cited SOW occurrences that need an explicit owner component selection. */
  component_confirmation_queue?: ComponentConfirmationCandidate[];
  /** Exact MAS choices backed by an active verified local release. */
  active_catalog_material_components?: ActiveCatalogMaterialComponentKey[];
  /** Cited project scope that remains unpriced and needs owner review. */
  component_scope_review_queue?: ComponentScopeReviewCandidate[];
  /** Verified Wage Determination rows the owner may select as role evidence. */
  selectable_wd_classifications?: SelectableWdClassification[];
  /** Present only for the server-owned standalone Price Estimator path. */
  standalone_mode?: boolean;
  project_brief?: StandaloneProjectBrief | null;
  /** Home Chat preparation saved to the existing standalone brief, or failed safely. */
  intake_prefill?: { status: 'applied' | 'unavailable'; notice?: string } | null;
  /** Browser-safe lifecycle status for the future standalone SOW upload. */
  standalone_scope_document?: StandaloneScopeDocument | null;
  /** Monotonic scope generation, including removed documents. */
  scope_document_revision?: number;
}
export interface SelectableWdClassification {
  classification_row_id: string;
  classification: string;
  classification_group?: string;
  base_wage?: string;
  fringe?: string;
  source_reference?: string;
  wage_determination_id: string;
  place: string;
  effective_date: string;
}

const normalizeSelectableWdClassifications = (value: unknown): SelectableWdClassification[] => (
  Array.isArray(value) ? value.flatMap((item): SelectableWdClassification[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    const classification_row_id = typeof raw.classification_row_id === 'string' ? raw.classification_row_id.trim() : '';
    const classification = typeof raw.classification === 'string' ? raw.classification.trim() : '';
    const wage_determination_id = typeof raw.wage_determination_id === 'string' ? raw.wage_determination_id.trim() : '';
    const place = typeof raw.place === 'string' ? raw.place.trim() : '';
    const effective_date = typeof raw.effective_date === 'string' ? raw.effective_date.trim() : '';
    if (!/^[a-z0-9][a-z0-9_-]{2,159}$/i.test(classification_row_id) || !classification || classification.length > 300 || !wage_determination_id || wage_determination_id.length > 120
      || !place || place.length > 160 || !/^\d{4}-\d{2}-\d{2}$/.test(effective_date)) return [];
    const optionalText = (field: string, maximum = 240): string | undefined => {
      const value = raw[field];
      return typeof value === 'string' && value.trim() && value.trim().length <= maximum ? value.trim() : undefined;
    };
    return [{ classification_row_id, classification, wage_determination_id, place, effective_date, ...(optionalText('classification_group', 300) ? { classification_group: optionalText('classification_group', 300) } : {}), ...(optionalText('base_wage') ? { base_wage: optionalText('base_wage') } : {}), ...(optionalText('fringe') ? { fringe: optionalText('fringe') } : {}), ...(optionalText('source_reference') ? { source_reference: optionalText('source_reference') } : {}) }];
  }).filter((item, index, all) => all.findIndex((candidate) => candidate.classification_row_id === item.classification_row_id) === index).slice(0, 200) : []
);

/** A standalone estimator's independent, revisioned project brief. */
export interface StandaloneProjectBrief {
  text: string;
  revision: number;
  content_hash?: string | null;
  updated_at?: string | null;
}

export interface StandaloneScopeDocument {
  upload_id: string;
  filename: string;
  content_type: 'application/pdf' | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' | 'text/plain';
  size_bytes: number;
  extraction_status: 'pending' | 'extracting' | 'ready' | 'rejected' | 'unreadable' | 'failed' | string;
  revision: number;
  updated_at?: string | null;
}

const normalizeStandaloneScopeDocument = (value: unknown): StandaloneScopeDocument | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const shortText = (candidate: unknown): string => typeof candidate === 'string' ? candidate.trim().slice(0, 1000) : '';
  const upload_id = shortText(raw.upload_id);
  const filename = shortText(raw.filename);
  const content_type = raw.content_type;
  const extraction_status = shortText(raw.extraction_status);
  const revision = Number(raw.revision);
  const size_bytes = Number(raw.size_bytes);
  if (!/^scope-upload-[a-f0-9]{16,64}$/i.test(upload_id) || !filename || filename.includes('/') || filename.includes('\\')
    || !['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'].includes(String(content_type))
    || !extraction_status || !Number.isInteger(revision) || revision < 1 || !Number.isInteger(size_bytes) || size_bytes < 0) return null;
  const updated_at = typeof raw.updated_at === 'string' ? raw.updated_at : null;
  return { upload_id, filename, content_type: content_type as StandaloneScopeDocument['content_type'], extraction_status, revision, size_bytes, updated_at };
};

export interface EstimateRevisionComparison {
  left: EstimateRevision;
  right: EstimateRevision;
}

type CategorizedError = Error & { error_code?: string; technical_detail?: string; response_data?: unknown };

function normalizeSourceStatuses(value: unknown): EstimateJobPhase[] {
  if (Array.isArray(value)) return value.filter((item): item is EstimateJobPhase => Boolean(item && typeof item === 'object'));
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value as Record<string, unknown>).map(([source, detail]) => (
    detail && typeof detail === 'object'
      ? { source, ...(detail as Record<string, unknown>) } as unknown as EstimateJobPhase
      : { source, status: String(detail ?? 'unknown') }
  ));
}

const UNIFIED_LANE_STATES = new Set<UnifiedSourceLaneState>([
  'queued', 'running', 'reused', 'complete', 'no_matching_evidence',
  'needs_contract_details', 'unavailable', 'failed', 'skipped', 'cancelled',
]);
const UNIFIED_OUTCOMES = new Set<UnifiedPriceRecommendationOutcome>([
  'recommended', 'evidence_conflict', 'benchmark_only', 'context_only', 'insufficient',
]);
const RANGE_TARGET_WARNING = 'Wide price range—review before relying on it.' as const;
const BTB_SOURCE_LABEL = 'DOE/NREL Buildings Technology Baseline (2024)' as const;
const BTB_LIMITATION = 'Planning baseline for a supported building-energy technology; not a project-specific quote or complete construction estimate.';
const BTB_NEGATIVE_INSTALLATION_WARNING = 'Source model produces a negative installation adjustment.' as const;
const publicMoney = (value: unknown): string | number | null => (
  (typeof value === 'number' && Number.isFinite(value) && value > 0)
  || (typeof value === 'string' && /^\d{1,12}(?:\.\d{1,6})?$/.test(value.trim()) && Number(value) > 0)
    ? value as string | number
    : null
);

const normalizeUnifiedText = (value: unknown): string | null => {
  const text = typeof value === 'string' ? value.trim() : '';
  // Reject machine identifiers and oversized/raw source material. Public copy
  // is intentionally short, authored display text only.
  return text && text.length <= 360 && !/^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/i.test(text) ? text : null;
};

const normalizeUnifiedRange = (value: unknown): UnifiedPlanningRangePublic | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const low = publicMoney(raw.low);
  const median = publicMoney(raw.median);
  const high = publicMoney(raw.high);
  const unitLabel = normalizeUnifiedText(raw.unit_label);
  const basisLabel = normalizeUnifiedText(raw.basis_label);
  const pricingDateLabel = normalizeUnifiedText(raw.pricing_date_label);
  if (low === null || median === null || high === null || Number(low) > Number(median) || Number(median) > Number(high)
    || !unitLabel || !basisLabel || !pricingDateLabel) return null;
  return { low, median, high, unit_label: unitLabel, basis_label: basisLabel, pricing_date_label: pricingDateLabel };
};

const normalizeConservativeComparableAwardPoint = (value: unknown): ConservativeComparableAwardPointPublic | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const amount = publicMoney(raw.amount);
  const basis_label = normalizeUnifiedText(raw.basis_label);
  const source_date_label = typeof raw.source_date_label === 'string' && /^20\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])$/.test(raw.source_date_label)
    ? raw.source_date_label
    : null;
  const policy_version = typeof raw.policy_version === 'string' && /^[a-z0-9][a-z0-9._-]{0,80}$/i.test(raw.policy_version)
    ? raw.policy_version
    : null;
  const selected = raw.selection_rule;
  const tier = raw.geography_tier;
  const count = raw.qualified_family_count;
  if (
    raw.reference_kind !== 'conservative_comparable_award_point' || amount === null
    || raw.source_label !== 'Previous federal awards — USAspending' || !basis_label || !source_date_label || !policy_version
    || (selected !== 'lowest_qualified_not_below_modeled_subtotal' && selected !== 'highest_qualified_when_all_below_modeled_subtotal')
    || (tier !== 'county_exact' && tier !== 'city_exact' && tier !== 'statewide')
    || !Number.isInteger(count) || (count as number) < 3 || (count as number) > 10_000
  ) return null;
  return {
    reference_kind: 'conservative_comparable_award_point', amount,
    source_label: 'Previous federal awards — USAspending', basis_label,
    selection_rule: selected, geography_tier: tier,
    qualified_family_count: count as number, source_date_label, policy_version,
  };
};

/** BTB allowances can validly be negative, unlike a planning-price range. */
const normalizeBtbMoney = (value: unknown): string | number | null => (
  (typeof value === 'number' && Number.isFinite(value))
  || (typeof value === 'string' && /^-?\d{1,12}(?:\.\d{1,6})?$/.test(value.trim()) && Number.isFinite(Number(value)))
    ? value as string | number
    : null
);

const normalizeBtbRange = (value: unknown, { allowNegative = false }: { allowNegative?: boolean } = {}): BtbTechnologyMoneyRangePublic | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const low = normalizeBtbMoney(raw.low);
  const median = normalizeBtbMoney(raw.median);
  const high = normalizeBtbMoney(raw.high);
  const unit_label = normalizeUnifiedText(raw.unit_label);
  if (low === null || median === null || high === null || !unit_label
    || Number(low) > Number(median) || Number(median) > Number(high)
    || (!allowNegative && (Number(low) <= 0 || Number(median) <= 0 || Number(high) <= 0))) return null;
  return { low, median, high, unit_label };
};

const normalizeBtbDetail = (value: unknown): BtbTechnologyDetailPublic | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const project_item_label = normalizeUnifiedText(raw.project_item_label);
  const technology_label = normalizeUnifiedText(raw.technology_label);
  const end_use_label = normalizeUnifiedText(raw.end_use_label);
  const fuel_label = normalizeUnifiedText(raw.fuel_label);
  const typical_unit_label = normalizeUnifiedText(raw.typical_unit_label);
  const source_quality_summary = normalizeUnifiedText(raw.source_quality_summary);
  const attribution = normalizeUnifiedText(raw.attribution);
  const limitation = normalizeUnifiedText(raw.limitation);
  const model_year = raw.model_year;
  const building_segment = raw.building_segment;
  const scenario_label = raw.scenario_label;
  const construction_scope_label = raw.construction_scope_label;
  const value_basis_label = raw.value_basis_label;
  const retail_benchmark = normalizeBtbRange(raw.retail_benchmark);
  const installation_allowance = normalizeBtbRange(raw.installation_allowance, { allowNegative: true });
  const installed_total = normalizeBtbRange(raw.installed_total);
  const installed_new_construction = normalizeBtbRange(raw.installed_new_construction);
  const installed_retrofit = normalizeBtbRange(raw.installed_retrofit);
  const hasSelectedScope = construction_scope_label === 'New construction' || construction_scope_label === 'Retrofit';
  const hasAmbiguousScope = !construction_scope_label && Boolean(installed_new_construction && installed_retrofit);
  const allowanceHasNegative = installation_allowance !== null && [installation_allowance.low, installation_allowance.median, installation_allowance.high]
    .some((amount) => Number(amount) < 0);
  if (!project_item_label || !technology_label || !attribution || limitation !== BTB_LIMITATION
    || !Number.isInteger(model_year) || ![2023, 2030, 2035, 2040, 2045, 2050].includes(model_year as number)
    || (building_segment !== 'Commercial' && building_segment !== 'Residential')
    || scenario_label !== 'Reference'
    || (value_basis_label !== 'Source-typical' && value_basis_label !== 'Project-scaled')
    || !retail_benchmark || !installation_allowance
    || (hasSelectedScope && (!installed_total || installed_new_construction || installed_retrofit))
    || (hasAmbiguousScope && (installed_total || !installed_new_construction || !installed_retrofit))
    || (!hasSelectedScope && !hasAmbiguousScope)
    || (raw.range_quality_warning !== undefined && raw.range_quality_warning !== RANGE_TARGET_WARNING)
    || (allowanceHasNegative && raw.negative_installation_warning !== BTB_NEGATIVE_INSTALLATION_WARNING)
    || (!allowanceHasNegative && raw.negative_installation_warning !== undefined)) return null;
  return {
    project_item_label,
    technology_label,
    ...(end_use_label ? { end_use_label } : {}),
    building_segment,
    ...(fuel_label ? { fuel_label } : {}),
    model_year: model_year as number,
    scenario_label: 'Reference',
    ...(hasSelectedScope ? { construction_scope_label } : {}),
    value_basis_label,
    ...(typical_unit_label ? { typical_unit_label } : {}),
    retail_benchmark,
    installation_allowance,
    ...(installed_total ? { installed_total } : {}),
    ...(installed_new_construction ? { installed_new_construction } : {}),
    ...(installed_retrofit ? { installed_retrofit } : {}),
    ...(raw.range_quality_warning === RANGE_TARGET_WARNING ? { range_quality_warning: RANGE_TARGET_WARNING } : {}),
    ...(allowanceHasNegative ? { negative_installation_warning: BTB_NEGATIVE_INSTALLATION_WARNING } : {}),
    ...(source_quality_summary ? { source_quality_summary } : {}),
    attribution,
    limitation: BTB_LIMITATION,
  };
};

/** Strict public allowlist for national BTB technology detail. */
export function normalizeBtbBreakdownPublic(value: unknown): BtbBreakdownPublic | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const status = raw.status;
  if ((status !== 'available' && status !== 'partial' && status !== 'no_matches' && status !== 'unavailable')
    || raw.heading !== 'Technology cost details'
    || raw.source_label !== BTB_SOURCE_LABEL
    || raw.geography_label !== 'National'
    || raw.value_year_label !== '2023 dollars'
    || !Array.isArray(raw.items) || raw.items.length > 50) return null;
  const items = raw.items.map(normalizeBtbDetail).filter((item): item is BtbTechnologyDetailPublic => item !== null);
  const unmatched_count = raw.unmatched_count;
  if (items.length !== raw.items.length
    || ((status === 'available' || status === 'partial') && items.length === 0)
    || ((status === 'no_matches' || status === 'unavailable') && items.length !== 0)
    || (unmatched_count !== undefined && (!Number.isInteger(unmatched_count) || (unmatched_count as number) < 0 || (unmatched_count as number) > 10_000))) return null;
  return {
    status,
    heading: 'Technology cost details',
    source_label: BTB_SOURCE_LABEL,
    geography_label: 'National',
    value_year_label: '2023 dollars',
    items,
    ...(typeof unmatched_count === 'number' && unmatched_count > 0 ? { unmatched_count } : {}),
  };
}

const GEORGIA_EVIDENCE_STATUSES = new Set<GeorgiaObservedEvidenceStatus>([
  'available', 'no_match', 'insufficient_evidence', 'not_configured',
]);

const normalizeObservedMoney = (value: unknown): string | number | null => (
  (typeof value === 'number' && Number.isFinite(value) && value >= 0)
  || (typeof value === 'string' && /^\d{1,12}(?:\.\d{1,6})?$/.test(value.trim()) && Number.isFinite(Number(value)))
    ? value as string | number
    : null
);

const normalizeGeorgiaObservedEvidenceItem = (value: unknown): GeorgiaObservedEvidenceItemPublic | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const work_item = safePublicText(raw.work_item);
  const original_description = safePublicText(raw.original_description);
  const unit = normalizeGeorgiaUnitText(raw.unit);
  const quantity = normalizeObservedMoney(raw.quantity);
  const unit_price = normalizeObservedMoney(raw.unit_price);
  const extended_price = normalizeObservedMoney(raw.extended_price);
  const price_date = safeDateText(raw.price_date);
  const project_location = safePublicText(raw.project_location);
  const source_reference = safePublicText(raw.source_reference);
  const caveats = Array.isArray(raw.caveats)
    ? raw.caveats.map(safePublicText).filter((item): item is string => Boolean(item)).slice(0, 8)
    : [];
  if (!work_item || !original_description || !unit || quantity === null || Number(quantity) <= 0
    || unit_price === null || extended_price === null || !price_date || !project_location || !source_reference
    || raw.currency !== 'USD' || (raw.evidence_status !== 'Awarded' && raw.evidence_status !== 'Final paid')
    || raw.source_label !== 'Approved Georgia public owner') return null;
  return {
    work_item, original_description, unit, quantity, unit_price, extended_price,
    currency: 'USD', price_date, evidence_status: raw.evidence_status,
    project_location, source_label: 'Approved Georgia public owner', source_reference, caveats,
  };
};

/** Strict browser boundary for Georgia public-owner observed line evidence. */
export function normalizeGeorgiaObservedEvidencePublic(value: unknown): GeorgiaObservedEvidencePublic | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const status = raw.status;
  if (!GEORGIA_EVIDENCE_STATUSES.has(status as GeorgiaObservedEvidenceStatus)
    || raw.heading !== 'Georgia public-owner price evidence'
    || raw.scope !== 'Georgia public-owner observed installed-price evidence'
    || !Array.isArray(raw.items) || raw.items.length > 50 || !Array.isArray(raw.warnings) || raw.warnings.length > 8) return null;
  const items = raw.items.map(normalizeGeorgiaObservedEvidenceItem)
    .filter((item): item is GeorgiaObservedEvidenceItemPublic => item !== null);
  const warnings = raw.warnings.map(safePublicText).filter((item): item is string => Boolean(item));
  const as_of = raw.as_of === undefined ? undefined : safeDateText(raw.as_of);
  if (items.length !== raw.items.length || warnings.length !== raw.warnings.length
    || (status === 'available' && items.length === 0)
    || (status !== 'available' && items.length > 0)
    || (raw.as_of !== undefined && !as_of)) return null;
  return {
    status: status as GeorgiaObservedEvidenceStatus,
    heading: 'Georgia public-owner price evidence',
    scope: 'Georgia public-owner observed installed-price evidence',
    ...(as_of ? { as_of } : {}), items, warnings,
  };
}

const normalizePublicHttpsUrl = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.trim() || value.length > 2_048) return null;
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
};

const normalizePublicPriceEvidenceItem = (value: unknown): PublicPriceEvidenceItemPublic | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const label = normalizeUnifiedText(raw.label);
  const amount = publicMoney(raw.amount);
  const evidence_tier = normalizeUnifiedText(raw.evidence_tier);
  const geography_label = normalizeUnifiedText(raw.geography_label);
  const price_date = safeDateText(raw.price_date);
  const limitation = normalizeUnifiedText(raw.limitation);
  const source_label = normalizeUnifiedText(raw.source_label);
  const source_url = normalizePublicHttpsUrl(raw.source_url);
  // Only reviewed, whole-award context evidence crosses this boundary. A
  // unit-price or installed-work record must use its own reviewed data path;
  // this prevents a browser payload from creating a component-cost line.
  if (!label || amount === null || !evidence_tier || raw.economic_scope !== 'Whole awarded project'
    || raw.contribution !== 'context_only' || !geography_label || !price_date || !limitation
    || !source_label || !source_url || raw.currency !== 'USD') return null;
  return {
    label,
    amount,
    currency: 'USD',
    evidence_tier,
    economic_scope: 'Whole awarded project',
    contribution: 'context_only',
    geography_label,
    price_date,
    limitation,
    source_label,
    source_url,
  };
};

/** Strict public allowlist for reviewed whole-award planning context. */
export function normalizePublicPriceEvidencePublic(value: unknown): PublicPriceEvidencePublic | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const status = raw.status;
  if ((status !== 'available' && status !== 'unavailable') || !Array.isArray(raw.items) || raw.items.length > 25) return null;
  const items = raw.items.map(normalizePublicPriceEvidenceItem)
    .filter((item): item is PublicPriceEvidenceItemPublic => item !== null);
  if (items.length !== raw.items.length || (status === 'available' && items.length === 0)
    || (status === 'unavailable' && items.length !== 0)) return null;
  return { status, items };
}

const normalizeUnifiedEvidenceSummary = (value: unknown): UnifiedEvidenceSummaryPublic | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const primary_summary = normalizeUnifiedText(raw.primary_summary);
  const corroboration_summary = normalizeUnifiedText(raw.corroboration_summary);
  const context_summary = normalizeUnifiedText(raw.context_summary);
  const result = { ...(primary_summary ? { primary_summary } : {}), ...(corroboration_summary ? { corroboration_summary } : {}), ...(context_summary ? { context_summary } : {}) };
  return Object.keys(result).length > 0 ? result : undefined;
};

const COMPARABLE_COHORT_STATUSES = new Set<ComparableCohortStatus>([
  'tight_supported', 'broader_supported', 'insufficient_similarity', 'context_only', 'source_unavailable',
]);
const COMPARABLE_COHORT_GEOGRAPHIC_TIERS = new Set<ComparableCohortGeographicTier>([
  'county', 'city', 'state',
]);

/** Keep only the small explanation intended for the award-evidence disclosure. */
const normalizeComparableCohortPublic = (value: unknown): ComparableCohortPublic | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const status = raw.status;
  const summary = normalizeUnifiedText(raw.summary);
  const geographic_tier = raw.geographic_tier;
  const selected_count = raw.selected_count;
  const excluded_count = raw.excluded_count;
  if (!COMPARABLE_COHORT_STATUSES.has(status as ComparableCohortStatus)
    || !summary
    || (geographic_tier !== undefined && !COMPARABLE_COHORT_GEOGRAPHIC_TIERS.has(geographic_tier as ComparableCohortGeographicTier))
    || !Number.isInteger(selected_count) || (selected_count as number) < 0 || (selected_count as number) > 10_000
    || (excluded_count !== undefined && (!Number.isInteger(excluded_count) || (excluded_count as number) < 0 || (excluded_count as number) > 10_000))) return null;
  return {
    status: status as ComparableCohortStatus,
    summary,
    ...(typeof geographic_tier === 'string' ? { geographic_tier: geographic_tier as ComparableCohortGeographicTier } : {}),
    selected_count: selected_count as number,
    ...(typeof excluded_count === 'number' ? { excluded_count } : {}),
  };
};

/** Strict public allowlist for the unified recommendation and drawer. */
export function normalizeUnifiedSourceResearchPublic(value: unknown): UnifiedSourceResearchPublic | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  if (raw.schema_version !== 'unified-source-research-v1' || !raw.recommendation || typeof raw.recommendation !== 'object') return null;
  const recommendationRaw = raw.recommendation as Record<string, unknown>;
  const outcome = recommendationRaw.outcome;
  const limitation = normalizeUnifiedText(recommendationRaw.limitation);
  if (!UNIFIED_OUTCOMES.has(outcome as UnifiedPriceRecommendationOutcome) || recommendationRaw.display_label !== 'Recommended planning price' || !limitation) return null;
  const range = normalizeUnifiedRange(recommendationRaw.range);
  const award_point = normalizeConservativeComparableAwardPoint(recommendationRaw.award_point);
  // A point is a separate public shape, never a degenerate range. Recommended
  // results have exactly one server-selected numeric presentation.
  if (
    (outcome === 'recommended') !== Boolean(range || award_point)
    || (range && award_point)
  ) return null;
  const displayShape = recommendationRaw.display_shape;
  const cohortCount = recommendationRaw.cohort_count;
  const validDisplayShape = displayShape === 'single' || displayShape === 'range' || displayShape === 'distribution' || displayShape === 'award_point';
  if (outcome === 'recommended' && (!validDisplayShape || !Number.isInteger(cohortCount)
    || (cohortCount as number) < 1 || (cohortCount as number) > 10_000
    || (displayShape === 'single' && cohortCount !== 1)
    || (displayShape === 'range' && cohortCount !== 2)
    || (displayShape === 'distribution' && (cohortCount as number) < 3)
    || (displayShape === 'award_point' && (!award_point || cohortCount !== award_point.qualified_family_count))
    || (range && displayShape === 'award_point')
    || (award_point && displayShape !== 'award_point'))) return null;
  const primary_source_label = normalizeUnifiedText(recommendationRaw.primary_source_label);
  const basis_label = normalizeUnifiedText(recommendationRaw.basis_label);
  const applicability_label = normalizeUnifiedText(recommendationRaw.applicability_label);
  // A conservative point is not a distribution and must never inherit a
  // percentile/range warning from an older recommendation payload.
  const warning = !award_point && recommendationRaw.range_quality_warning === RANGE_TARGET_WARNING ? RANGE_TARGET_WARNING : null;
  const recommendation: UnifiedPriceRecommendationPublic = {
    outcome: outcome as UnifiedPriceRecommendationOutcome,
    display_label: 'Recommended planning price',
    ...(primary_source_label ? { primary_source_label } : {}),
    ...(range ? { range } : {}),
    ...(award_point ? { award_point } : {}),
    ...(outcome === 'recommended' ? { display_shape: displayShape as 'single' | 'range' | 'distribution' | 'award_point', cohort_count: cohortCount as number } : {}),
    ...(warning ? { range_quality_warning: warning } : {}),
    ...(basis_label ? { basis_label } : {}),
    ...(applicability_label ? { applicability_label } : {}),
    ...(normalizeUnifiedEvidenceSummary(recommendationRaw.evidence_summary) ? { evidence_summary: normalizeUnifiedEvidenceSummary(recommendationRaw.evidence_summary) } : {}),
    limitation,
  };
  const drawerRaw = raw.evidence_drawer && typeof raw.evidence_drawer === 'object' && !Array.isArray(raw.evidence_drawer)
    ? raw.evidence_drawer as Record<string, unknown> : {};
  const evidence_drawer: UnifiedEvidenceDrawerPublic = {
    ...(normalizeUnifiedText(drawerRaw.primary_summary) ? { primary_summary: normalizeUnifiedText(drawerRaw.primary_summary)! } : {}),
    ...(normalizeUnifiedText(drawerRaw.corroboration_summary) ? { corroboration_summary: normalizeUnifiedText(drawerRaw.corroboration_summary)! } : {}),
    ...(normalizeUnifiedText(drawerRaw.context_summary) ? { context_summary: normalizeUnifiedText(drawerRaw.context_summary)! } : {}),
    ...(normalizeUnifiedText(drawerRaw.limitation) ? { limitation: normalizeUnifiedText(drawerRaw.limitation)! } : {}),
  };
  const lanes = Array.isArray(raw.lanes) ? raw.lanes.flatMap((item): UnifiedSourceLaneProgress[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const lane = item as Record<string, unknown>;
    const label = normalizeUnifiedText(lane.label);
    const state = lane.state;
    if (!label || !UNIFIED_LANE_STATES.has(state as UnifiedSourceLaneState)) return [];
    const message = normalizeUnifiedText(lane.message);
    const evidenceCount = typeof lane.evidence_count === 'number' && Number.isInteger(lane.evidence_count) && lane.evidence_count >= 0 && lane.evidence_count <= 100000
      ? lane.evidence_count : undefined;
    return [{ label, state: state as UnifiedSourceLaneState, ...(message ? { message } : {}), ...(evidenceCount !== undefined ? { evidence_count: evidenceCount } : {}) }];
  }) : [];
  const sources_checked = Array.isArray(raw.sources_checked) ? raw.sources_checked.flatMap((item): UnifiedSourceCheckedPublic[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const source = item as Record<string, unknown>;
    const label = normalizeUnifiedText(source.label);
    const detail = normalizeUnifiedText(source.detail);
    const outcome = source.outcome;
    const allowedOutcome = outcome === 'consulted' || outcome === 'reused'
      || outcome === 'no_matching_evidence' || outcome === 'unavailable'
      || outcome === 'not_available' || outcome === 'not_applicable';
    const contribution = source.contribution;
    const allowedContribution = contribution === 'used' || contribution === 'context' || contribution === 'not_used';
    const evidenceCount = typeof source.evidence_count === 'number' && Number.isInteger(source.evidence_count)
      && source.evidence_count >= 0 && source.evidence_count <= 500 ? source.evidence_count : undefined;
    const asOf = typeof source.as_of === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(source.as_of)
      && !Number.isNaN(Date.parse(source.as_of)) ? source.as_of : undefined;
    return label ? [{ label, ...(detail ? { detail } : {}), ...(allowedOutcome ? { outcome } : {}),
      ...(allowedContribution ? { contribution } : {}), ...(evidenceCount !== undefined ? { evidence_count: evidenceCount } : {}),
      ...(asOf ? { as_of: asOf } : {}) }] : [];
  }).slice(0, 20) : [];
  const btb_breakdown = normalizeBtbBreakdownPublic(raw.btb_breakdown);
  const georgia_observed_evidence = normalizeGeorgiaObservedEvidencePublic(raw.georgia_observed_evidence);
  const public_price_evidence = normalizePublicPriceEvidencePublic(raw.public_price_evidence);
  const approximate_cost_plan = normalizeApproximateCostPlan(raw.approximate_cost_plan);
  const comparable_cohort = normalizeComparableCohortPublic(raw.comparable_cohort);
  return {
    schema_version: 'unified-source-research-v1', recommendation, evidence_drawer, lanes,
    ...(raw.sources_checked !== undefined ? { sources_checked } : {}),
    ...(raw.approximate_cost_plan !== undefined ? { approximate_cost_plan } : {}),
    ...(raw.btb_breakdown !== undefined ? { btb_breakdown } : {}),
    ...(raw.georgia_observed_evidence !== undefined ? { georgia_observed_evidence } : {}),
    ...(raw.public_price_evidence !== undefined ? { public_price_evidence } : {}),
    ...(raw.comparable_cohort !== undefined ? { comparable_cohort } : {}),
  };
}

/** Discard all proposed match facts except the compact owner-review labels and opaque IDs. */
export function normalizeSourceMatchProposalPublic(value: unknown): SourceMatchProposalPublic | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  if (!Number.isInteger(raw.proposal_revision) || (raw.proposal_revision as number) < 0 || !Array.isArray(raw.candidates)) return null;
  const candidates = raw.candidates.flatMap((value): SourceMatchReviewCandidate[] => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    const candidate = value as Record<string, unknown>;
    const proposal_id = typeof candidate.proposal_id === 'string' && /^[A-Za-z0-9_-]{1,160}$/.test(candidate.proposal_id) ? candidate.proposal_id : null;
    const review_label = normalizeUnifiedText(candidate.review_label);
    const scope_label = normalizeUnifiedText(candidate.scope_label);
    const measurement_label = normalizeUnifiedText(candidate.measurement_label);
    return proposal_id && review_label ? [{ proposal_id, review_label, ...(scope_label ? { scope_label } : {}), ...(measurement_label ? { measurement_label } : {}) }] : [];
  });
  return candidates.length > 0 ? { proposal_revision: raw.proposal_revision as number, candidates } : null;
}

function normalizeJob(value: any): EstimateJob | null {
  if (!value || typeof value !== 'object') return null;
  return { ...value, source_statuses: normalizeSourceStatuses(value.source_statuses), unified_source_research: normalizeUnifiedSourceResearchPublic(value.unified_source_research) } as EstimateJob;
}

function normalizeCapabilities(value: unknown): EstimateCapabilities {
  const capabilities = value && typeof value === 'object' ? value as EstimateCapabilities : { applicable: false, enabled: false, calculation_available: false, export_available: false, path_policy_version: 0 };
  const primary = capabilities.cost_data_primary;
  const candidateStatus = primary && typeof primary === 'object'
    ? safeText((primary as unknown as Record<string, unknown>).status)
    : null;
  const safeStatuses = new Set<CostDataPrimaryStatus>([
    'configured', 'eligible', 'not_configured', 'rights_pending', 'rights_restricted', 'source_unavailable', 'no_eligible_coverage',
  ]);
  const normalizedPrimary = primary && typeof primary === 'object'
    ? { status: safeStatuses.has(candidateStatus as CostDataPrimaryStatus) ? candidateStatus as CostDataPrimaryStatus : 'not_configured' }
    : primary;
  return {
    ...capabilities,
    ...(capabilities.approximate_cost_plan !== undefined ? { approximate_cost_plan: {
      supported_recipes: (Array.isArray(capabilities.approximate_cost_plan?.supported_recipes) ? capabilities.approximate_cost_plan.supported_recipes : []).slice(0, 30).filter(recipe => recipe && typeof recipe.recipe_key === 'string' && typeof recipe.label === 'string' && typeof recipe.scope === 'string').map(recipe => ({ recipe_key: recipe.recipe_key, label: recipe.label, scope: recipe.scope, allowed_units: Array.isArray(recipe.allowed_units) ? recipe.allowed_units.filter(unit => typeof unit === 'string') : [] })),
    } } : {}),
    ...(normalizedPrimary !== undefined ? { cost_data_primary: normalizedPrimary } : {}),
    ...(capabilities.davis_bacon_schedule !== undefined
      ? { davis_bacon_schedule: normalizeDavisBaconScheduleCapability(capabilities.davis_bacon_schedule) }
      : {}),
  };
}

function rangeBase(value: any): unknown {
  return value && typeof value === 'object' && 'base' in value ? value.base : value;
}

function normalizeWorkItem(value: any): Record<string, any> {
  if (!value || typeof value !== 'object') return value;
  const costComponents = value.cost_components && typeof value.cost_components === 'object'
    ? value.cost_components
    : {
      labor: rangeBase(value.labor),
      material: rangeBase(value.material),
      equipment: rangeBase(value.equipment),
      subcontract: rangeBase(value.subcontract),
      allowance: rangeBase(value.allowance) ?? '0',
    };
  return {
    ...value,
    id: value.id ?? value.work_item_id,
    cost_components: costComponents,
    low: value.low ?? value.total?.low,
    base: value.base ?? value.total?.base,
    high: value.high ?? value.total?.high,
  };
}

const COST_DATA_DECISIONS = new Set<CostDataDecision>([
  'direct_cost_estimate', 'partial_itemized_estimate', 'insufficient_item_evidence', 'award_research_only',
]);
const COST_DATA_PRICING_KINDS = new Set<CostDataPricingKind>([
  'installed_work', 'componentized', 'unresolved', 'manual_allowance',
]);
const COST_DATA_COMPONENT_STATES = new Set<CostDataComponentState>([
  'priced', 'included_in_installed_work', 'not_separately_reported', 'not_available',
]);

const safeText = (value: unknown): string | null => (
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
);

/** Public copy must not accidentally expose underscore-delimited storage codes. */
const safePublicText = (value: unknown): string | null => {
  const text = safeText(value);
  return text && !/^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/i.test(text) ? text : null;
};

const safeDateText = (value: unknown): string | null => {
  const text = safeText(value);
  return text && /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)?$/.test(text)
    ? text
    : null;
};

const DAVIS_BACON_CAPABILITY_REASONS = new Set<DavisBaconScheduleCapabilityReason>([
  'not_configured', 'not_applicable', 'prerequisites_required',
]);
const DAVIS_BACON_SCHEDULE_STATUSES = new Set<DavisBaconScheduleStatus>([
  'available', 'review_required', 'rejected', 'reference_found',
]);
const DAVIS_BACON_REVIEW_STATES = new Set<DavisBaconScheduleReviewState>([
  'validated', 'approved', 'review_required', 'rejected', 'not_required',
]);
const DAVIS_BACON_CONSTRUCTION_TYPES = new Set<DavisBaconConstructionType>([
  'Building', 'Residential', 'Highway', 'Heavy',
]);
function normalizeDavisBaconScheduleCapability(value: unknown): DavisBaconScheduleCapability {
  const record = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const reason = safeText(record.reason) as DavisBaconScheduleCapabilityReason | null;
  return {
    available: record.available === true,
    reason: reason && DAVIS_BACON_CAPABILITY_REASONS.has(reason) ? reason : null,
    ...(record.can_view_details === true ? { can_view_details: true } : {}),
    ...(record.can_review === true ? { can_review: true } : {}),
  };
}

function normalizeDavisBaconScheduleSummary(value: unknown): DavisBaconScheduleSummary | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const status = safeText(record.status) as DavisBaconScheduleStatus | null;
  if (!status || !DAVIS_BACON_SCHEDULE_STATUSES.has(status)) return null;
  const reviewState = safeText(record.review_state) as DavisBaconScheduleReviewState | null;
  const constructionType = safeText(record.construction_type) as DavisBaconConstructionType | null;
  const determinationId = safeText(record.determination_id);
  const revision = safeText(record.revision);
  const state = safeText(record.state);
  const county = safePublicText(record.county);
  const referenceDate = safeText(record.reference_date);
  const retrievedAt = safeText(record.retrieved_at);
  const classificationCount = record.classification_count;
  const publicationDate = safeText(record.publication_date);
  const effectiveDate = safeText(record.effective_date);
  const sourceName = safeText(record.source_name);
  const determinationUrl = safeText(record.determination_url);
  const extractionStatus = safeText(record.extraction_status);
  if (!determinationId || !revision || !state || !/^[A-Z]{2}$/.test(state)
    || !county || !constructionType || !DAVIS_BACON_CONSTRUCTION_TYPES.has(constructionType)
    || !referenceDate || !retrievedAt || !reviewState || !DAVIS_BACON_REVIEW_STATES.has(reviewState)
    || typeof classificationCount !== 'number' || !Number.isInteger(classificationCount)
    || classificationCount < 0 || classificationCount > 500) return null;
  const validStatusReviewPair = (status === 'available' && (reviewState === 'validated' || reviewState === 'approved'))
    || (status === 'review_required' && reviewState === 'review_required')
    || (status === 'rejected' && reviewState === 'rejected')
    || (status === 'reference_found' && reviewState === 'not_required'
      && classificationCount === 0 && publicationDate !== null && effectiveDate !== null
      && sourceName === 'SAM.gov'
      && determinationUrl === `https://sam.gov/wage-determination/${determinationId}/${revision}`
      && extractionStatus === 'deferred');
  if (!validStatusReviewPair) return null;
  return {
    ...(typeof record.schedule_id === 'string' && /^schedule-[A-Za-z0-9_-]+$/.test(record.schedule_id) ? { schedule_id: record.schedule_id } : {}),
    ...(status === 'review_required' && typeof record.review_token === 'string' && /^review-[A-Za-z0-9_-]+$/.test(record.review_token) ? { review_token: record.review_token } : {}),
    determination_id: determinationId,
    revision,
    state,
    county,
    construction_type: constructionType,
    reference_date: referenceDate,
    retrieved_at: retrievedAt,
    ...(publicationDate ? { publication_date: publicationDate } : {}),
    ...(effectiveDate ? { effective_date: effectiveDate } : {}),
    ...(sourceName === 'SAM.gov' ? { source_name: sourceName } : {}),
    ...(determinationUrl === `https://sam.gov/wage-determination/${determinationId}/${revision}` ? { determination_url: determinationUrl } : {}),
    ...(extractionStatus === 'deferred' ? { extraction_status: extractionStatus } : {}),
    status,
    review_state: reviewState,
    classification_count: classificationCount,
  };
}

const safeMoney = (value: unknown): string | null => (
  typeof value === 'string' && /^\d{1,8}(?:\.\d{1,2})?$/.test(value) ? value : null
);

function normalizeDavisBaconScheduleDetails(value: unknown): DavisBaconScheduleDetails | null {
  const summary = normalizeDavisBaconScheduleSummary(value);
  if (!summary || !summary.schedule_id || !value || typeof value !== 'object') return null;
  const rows = (value as Record<string, unknown>).rows;
  if (!Array.isArray(rows) || rows.length > 500) return null;
  const safeRows = rows.flatMap((row): DavisBaconScheduleDetailRow[] => {
    if (!row || typeof row !== 'object') return [];
    const record = row as Record<string, unknown>;
    const classification = safePublicText(record.classification);
    const baseWage = safeMoney(record.base_wage);
    const fringe = safeMoney(record.fringe);
    const total = safeMoney(record.total_hourly);
    if (!classification || !baseWage || !fringe || !total || record.unit !== 'hour') return [];
    const footnotes = Array.isArray(record.footnote_identifiers)
      ? record.footnote_identifiers.filter((item): item is string => Boolean(safePublicText(item))).map((item) => safePublicText(item)!)
      : [];
    return [{ classification, base_wage: baseWage, fringe, total_hourly: total, unit: 'hour',
      ...(safePublicText(record.group) ? { group: safePublicText(record.group)! } : {}),
      ...(footnotes.length ? { footnote_identifiers: footnotes } : {}),
    }];
  });
  if (safeRows.length !== rows.length) return null;
  return { ...summary, schedule_id: summary.schedule_id, rows: safeRows };
}

function normalizeDavisBaconScheduleJob(value: unknown): DavisBaconScheduleJob | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const jobId = safeText(record.job_id);
  const status = safeText(record.status) as DavisBaconScheduleJob['status'] | null;
  const failureReason = safeText(record.failure_reason);
  if (!jobId || !status || !['pending', 'running', 'completed', 'failed', 'stale', 'cancelled'].includes(status)) return null;
  return {
    job_id: jobId,
    status,
    ...(safePublicText(record.phase) ? { phase: safePublicText(record.phase)! } : {}),
    ...(safeText(record.created_at) ? { created_at: safeText(record.created_at)! } : {}),
    ...(safeText(record.completed_at) ? { completed_at: safeText(record.completed_at)! } : {}),
    ...(safeText(record.cancelled_at) ? { cancelled_at: safeText(record.cancelled_at)! } : {}),
    ...(record.retryable === true ? { retryable: true } : {}),
    ...(failureReason === 'no_matching_determination' || failureReason === 'source_unavailable' || failureReason === 'lookup_failed' ? { failure_reason: failureReason } : {}),
    ...(record.saved_schedule_reused === true ? { saved_schedule_reused: true } : {}),
    ...(normalizeDavisBaconScheduleSummary(record.schedule) ? { schedule: normalizeDavisBaconScheduleSummary(record.schedule)! } : {}),
  };
}

function normalizeCostDataProvenance(value: unknown): CostDataProvenance | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const sourceLabel = safePublicText(record.source_label);
  if (!sourceLabel) return null;
  const geographyFit = safeText(record.geography_fit);
  const timeAdjustmentRecord = record.time_adjustment && typeof record.time_adjustment === 'object'
    ? record.time_adjustment as Record<string, unknown>
    : null;
  const adjustmentStatus = timeAdjustmentRecord ? safeText(timeAdjustmentRecord.status) : null;
  const time_adjustment = timeAdjustmentRecord
    && adjustmentStatus === 'adjusted'
    && safePublicText(timeAdjustmentRecord.source_label)
    && safePublicText(timeAdjustmentRecord.observation_period)
    && safePublicText(timeAdjustmentRecord.reference_period)
    ? {
      source_label: safePublicText(timeAdjustmentRecord.source_label)!,
      status: 'adjusted' as const,
      observation_period: safePublicText(timeAdjustmentRecord.observation_period)!,
      reference_period: safePublicText(timeAdjustmentRecord.reference_period)!,
    }
    : null;
  return {
    source_label: sourceLabel,
    effective_date: safeDateText(record.effective_date),
    geography_fit: geographyFit === 'county' || geographyFit === 'state' ? geographyFit : null,
    display_class: safeText(record.display_class),
    freshness_status: safeText(record.freshness_status) === 'current'
      ? 'current'
      : safeText(record.freshness_status) === 'stale'
        ? 'stale'
        : safeText(record.freshness_status) === 'not_available'
          ? 'not_available'
          : null,
    time_adjustment,
    selected_observation_count: Number.isSafeInteger(Number(record.selected_observation_count)) && Number(record.selected_observation_count) > 0
      ? Number(record.selected_observation_count)
      : null,
    observation_start_date: safeDateText(record.observation_start_date),
    observation_end_date: safeDateText(record.observation_end_date),
    snapshot_published_at: safeDateText(record.snapshot_published_at),
  };
}

function normalizeCostDataMoneyRange(value: unknown): CostDataMoneyRange | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const valid = (candidate: unknown): candidate is string | number => (
    (typeof candidate === 'number' && Number.isFinite(candidate) && candidate >= 0)
    || (typeof candidate === 'string' && /^\d+(?:\.\d+)?$/.test(candidate.trim()))
  );
  return valid(record.low) && valid(record.base) && valid(record.high)
    ? { low: record.low, base: record.base, high: record.high }
    : null;
}

function normalizeWorkItemCostData(value: unknown): WorkItemCostData | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const pricingKind = safeText(record.pricing_kind) as CostDataPricingKind | null;
  if (!pricingKind || !COST_DATA_PRICING_KINDS.has(pricingKind)) return null;
  const rawStates = record.component_states && typeof record.component_states === 'object'
    ? record.component_states as Record<string, unknown> : {};
  const component_states = Object.fromEntries(
    ['labor', 'material', 'equipment', 'subcontract']
      .filter((key) => COST_DATA_COMPONENT_STATES.has(rawStates[key] as CostDataComponentState))
      .map((key) => [key, rawStates[key] as CostDataComponentState]),
  ) as WorkItemCostData['component_states'];
  return {
    pricing_kind: pricingKind,
    component_states,
    provenance: normalizeCostDataProvenance(record.provenance),
    unit_range: normalizeCostDataMoneyRange(record.unit_range),
    total_range: normalizeCostDataMoneyRange(record.total_range),
  };
}

function normalizeRevisionWorkItem(value: any): Record<string, any> {
  const normalized = normalizeWorkItem(value);
  const costDataWire = value?.cost_data && typeof value.cost_data === 'object'
    ? value.cost_data as Record<string, unknown>
    : value as Record<string, unknown>;
  const pricingKind = costDataWire.pricing_kind ?? costDataWire.pricing_basis;
  const componentStates = costDataWire.component_states ?? costDataWire.component_inclusion;
  const provenance = costDataWire.provenance ?? costDataWire.provenance_summary;
  return {
    ...normalized,
    cost_data: normalizeWorkItemCostData({
      pricing_kind: pricingKind,
      component_states: componentStates && typeof componentStates === 'object'
        ? {
          ...(componentStates as Record<string, unknown>),
          material: (componentStates as Record<string, unknown>).material
            ?? (componentStates as Record<string, unknown>).materials,
        }
        : componentStates,
      provenance,
      unit_range: value?.unit_cost,
      total_range: value?.installed_work ?? value?.total,
    }),
  };
}

function normalizeCostDataSummary(value: unknown): CostDataSummary | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const decision = safeText(record.decision) as CostDataDecision | null;
  if (!decision || !COST_DATA_DECISIONS.has(decision)) return null;
  const count = (candidate: unknown): number => {
    const numeric = Number(candidate);
    return Number.isSafeInteger(numeric) && numeric >= 0 ? numeric : 0;
  };
  const unresolved_items = Array.isArray(record.unresolved_items)
    ? record.unresolved_items.flatMap((item): CostDataUnresolvedItem[] => {
      if (!item || typeof item !== 'object') return [];
      const unresolved = item as Record<string, unknown>;
      const label = safeText(unresolved.label);
      return label && unresolved.next_action === 'add_allowance_or_exclude'
        ? [{ work_item_id: safeText(unresolved.work_item_id) ?? undefined, label, next_action: 'add_allowance_or_exclude' }]
        : [];
    }) : [];
  const freshness = safeText(record.source_freshness);
  const provenance = (Array.isArray(record.provenance) ? record.provenance : [record.provenance])
    .map(normalizeCostDataProvenance)
    .filter((item): item is CostDataProvenance => item !== null);
  return {
    decision,
    covered_item_count: count(record.covered_item_count),
    included_item_count: count(record.included_item_count),
    unresolved_items,
    ...(freshness === 'current' || freshness === 'stale' || freshness === 'not_available' ? { source_freshness: freshness } : {}),
    provenance,
  };
}

/**
 * Keep the browser boundary allowlisted. A completed revision may contain a
 * BTB panel only when the server's public projection is complete and exactly
 * describes the approved national planning-baseline role. Unknown status,
 * provenance, raw-source, or component fields are intentionally discarded.
 */
export function normalizeBtbPlanningBaselinePublic(value: unknown): BtbPlanningBaselinePublic | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (record.source_label !== BTB_SOURCE_LABEL || record.evidence_type !== 'National planning baseline'
    || record.geography_label !== 'National') return null;
  const building_segment = safeText(record.building_segment);
  const cost_basis_label = safeText(record.cost_basis_label);
  const technology_label = safePublicText(record.technology_label);
  const value_year_label = safePublicText(record.value_year_label);
  const attribution = safePublicText(record.attribution);
  const scenario_label = safePublicText(record.scenario_label);
  const limitationValues = record.limitations;
  const limitations = Array.isArray(limitationValues) && limitationValues.length === 1
    && limitationValues[0] === BTB_LIMITATION ? [BTB_LIMITATION] as [string] : null;
  const price = record.value && typeof record.value === 'object' && !Array.isArray(record.value)
    ? record.value as Record<string, unknown> : null;
  const amount = price?.amount;
  const validAmount = (typeof amount === 'number' && Number.isFinite(amount) && amount >= 0)
    || (typeof amount === 'string' && /^\d{1,8}(?:\.\d{1,2})?$/.test(amount.trim()));
  const unit_label = safePublicText(price?.unit_label);
  if (!technology_label || !value_year_label || !attribution || !limitations || !validAmount || !unit_label
    || price?.currency !== 'USD'
    || (building_segment !== 'commercial' && building_segment !== 'residential')
    || (cost_basis_label !== 'Installed' && cost_basis_label !== 'Retail')) return null;
  return {
    source_label: BTB_SOURCE_LABEL,
    evidence_type: 'National planning baseline',
    technology_label,
    building_segment,
    cost_basis_label,
    value: { amount: amount as string | number, currency: 'USD', unit_label },
    value_year_label,
    ...(scenario_label ? { scenario_label } : {}),
    geography_label: 'National',
    attribution,
    limitations,
  };
}

function normalizeRevision(value: any): EstimateRevision | null {
  if (!value || typeof value !== 'object') return null;
  const totals = value.totals && typeof value.totals === 'object'
    ? { ...value.totals, ...(value.totals.total && typeof value.totals.total === 'object' ? value.totals.total : {}) }
    : value.totals;
  const comparables = value.comparables && typeof value.comparables === 'object'
    ? { ...value.comparables, ...(value.comparables.range && typeof value.comparables.range === 'object' ? value.comparables.range : {}) }
    : value.comparables;
  const workItems = Array.isArray(value.work_items)
    ? value.work_items.map(normalizeRevisionWorkItem)
    : value.work_items;
  return {
    ...value,
    totals,
    comparables,
    work_items: workItems,
    source_statuses: normalizeSourceStatuses(value.source_statuses),
    ...(value.approximate_cost_plan !== undefined ? { approximate_cost_plan: normalizeApproximateCostPlan(value.approximate_cost_plan) } : {}),
    cost_data_summary: normalizeCostDataSummary(value.cost_data_summary),
    ...(value.btb_planning_baseline !== undefined
      ? { btb_planning_baseline: normalizeBtbPlanningBaselinePublic(value.btb_planning_baseline) }
      : {}),
    ...(value.unified_source_research !== undefined
      ? { unified_source_research: normalizeUnifiedSourceResearchPublic(value.unified_source_research) }
      : {}),
  } as EstimateRevision;
}

async function unwrap<T>(promise: Promise<ApiResponse<T>>): Promise<T> {
  const response = await promise;
  if (!response.success) {
    const error = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted) as CategorizedError;
    error.error_code = response.error_code;
    error.technical_detail = response.technical_detail;
    error.response_data = response.data;
    throw error;
  }
  return (response.data ?? response) as T;
}

const root = (flowId: string) => `/api/construction-price-estimator/${encodeURIComponent(flowId)}`;

const normalizeOptionalLocation = (value: unknown): string | null => (
  typeof value === 'string' && value.trim() ? value.trim() : null
);

const normalizeProjectLocation = (location: EstimateDraft['project_location']): EstimateDraft['project_location'] => ({
  country: typeof location.country === 'string' && location.country.trim() ? location.country.trim() : 'US',
  state: normalizeOptionalLocation(location.state),
  county: normalizeOptionalLocation(location.county),
  city: normalizeOptionalLocation(location.city),
  postal_code: normalizeOptionalLocation(location.postal_code),
});


const normalizePrefillDraft = (prefill: PriceSearchPrefill | null | undefined) => (
  prefill ? { ...prefill,
    ...(prefill.partial_draft ? {
      partial_draft: stripLegacyWageDeterminationFields(prefill.partial_draft),
    } : {}),
  } : prefill
);

export const getConstructionEstimate = async (flowId: string): Promise<EstimateLoadData> => {
  const loaded = await unwrap<any>(apiCall<any>(root(flowId), 'GET'));
  const readiness = Array.isArray(loaded.readiness)
    ? { blockers: loaded.readiness, ready_to_calculate: loaded.readiness.length === 0 }
    : loaded.readiness ?? { blockers: [], ready_to_calculate: false };
  return {
    ...loaded,
    capabilities: normalizeCapabilities(loaded.capabilities),
    draft: stripLegacyWageDeterminationFields(loaded.draft),
    prefill: normalizePrefillDraft(loaded.prefill),
    site_staffing_prefill_needed: loaded.site_staffing_prefill_needed === true,
    readiness,
    trusted_basis: loaded.trusted_basis ?? loaded.upstream_source_hashes,
    active_revision: normalizeRevision(loaded.active_revision),
    job: normalizeJob(loaded.job),
    source_statuses: normalizeSourceStatuses(loaded.source_statuses),
    cost_data_summary: normalizeCostDataSummary(loaded.cost_data_summary),
    unified_source_research: normalizeUnifiedSourceResearchPublic(loaded.unified_source_research),
    source_match_proposal: normalizeSourceMatchProposalPublic(loaded.source_match_proposal),
    davis_bacon_schedule: normalizeDavisBaconScheduleSummary(loaded.davis_bacon_schedule),
    davis_bacon_schedule_job: normalizeDavisBaconScheduleJob(loaded.davis_bacon_schedule_job),
    component_confirmation_queue: normalizeComponentConfirmationCandidates(loaded.component_confirmation_queue),
    active_catalog_material_components: normalizeActiveCatalogMaterialComponentKeys(loaded.active_catalog_material_components),
    component_scope_review_queue: normalizeComponentScopeReviewCandidates(loaded.component_scope_review_queue),
    selectable_wd_classifications: normalizeSelectableWdClassifications(loaded.selectable_wd_classifications),
    standalone_scope_document: normalizeStandaloneScopeDocument(loaded.standalone_scope_document),
    scope_document_revision: Number.isSafeInteger(loaded.scope_document_revision) && loaded.scope_document_revision >= 0
      ? loaded.scope_document_revision : normalizeStandaloneScopeDocument(loaded.standalone_scope_document)?.revision ?? 0,
  } as EstimateLoadData;
};

export const saveConstructionEstimateDraft = async (flowId: string, draft: EstimateDraftRequest): Promise<EstimateDraft | EstimateLoadData> => {
  const saved = await unwrap(apiCall<EstimateDraft | EstimateLoadData>(`${root(flowId)}/draft`, 'PUT', {
    ...draft,
    project_location: normalizeProjectLocation(draft.project_location),
  }));
  if (saved && typeof saved === 'object' && 'capabilities' in saved) {
    const loaded = saved as EstimateLoadData;
    return { ...loaded, draft: stripLegacyWageDeterminationFields(loaded.draft),
      prefill: normalizePrefillDraft(loaded.prefill) };
  }
  return stripLegacyWageDeterminationFields(saved as EstimateDraft);
};

/** Save the standalone project brief without coupling it to Contract Details. */
export const saveConstructionEstimatorProjectBrief = (
  flowId: string,
  expectedRevision: number,
  text: string,
): Promise<StandaloneProjectBrief> => unwrap(apiCall<StandaloneProjectBrief>(`${root(flowId)}/brief`, 'PUT', {
  expected_revision: expectedRevision,
  text,
}));

/** The backend owns file validation, private storage, and text extraction. */
export const uploadConstructionEstimatorScopeDocument = async (flowId: string, file: File, expectedRevision: number): Promise<StandaloneScopeDocument> => {
  if (!Number.isInteger(expectedRevision) || expectedRevision < 0) throw new Error('The saved SOW revision is invalid. Reload and try again.');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('expected_revision', String(expectedRevision));
  const response = await fetchWithChatFeedback(`${API_BASE_URL}${root(flowId)}/scope-document`, { method: 'POST', body: formData, credentials: 'include' });
  if (!response.ok) throw new Error('The SOW could not be uploaded. Use a PDF, DOCX, or UTF-8 text file and try again.');
  const body = await response.json() as { data?: unknown } | unknown;
  const document = normalizeStandaloneScopeDocument((body as { data?: unknown })?.data ?? body);
  if (!document) throw new Error('The SOW upload did not return a usable document status.');
  return document;
};

export const removeConstructionEstimatorScopeDocument = async (flowId: string, uploadId: string, expectedRevision: number): Promise<number> => {
  if (!/^scope-upload-[a-f0-9]{16,64}$/i.test(uploadId) || !Number.isInteger(expectedRevision) || expectedRevision < 1) throw new Error('The saved SOW reference is invalid. Reload and try again.');
  const removed = await unwrap(apiCall<{ removed: boolean; scope_document_revision: number }>(`${root(flowId)}/scope-document/${encodeURIComponent(uploadId)}`, 'DELETE', { expected_revision: expectedRevision }));
  if (removed?.removed !== true || !Number.isSafeInteger(removed.scope_document_revision) || removed.scope_document_revision <= expectedRevision) throw new Error('The SOW removal did not return a usable revision. Reload before uploading again.');
  return removed.scope_document_revision;
};

/**
 * Request a server-composed proposal from trusted Stage-604 data.
 * No SOW, salient characteristics, search context, model choice, or location
 * candidate is accepted from the browser.
 */
export const prepareConstructionPriceSearchDetails = (
  flowId: string,
  expectedDraftRevision: number | null,
  mode: PriceSearchPrefillMode,
): Promise<PriceSearchPrefillJob> => unwrap(apiCall<PriceSearchPrefillJob>(
  `${root(flowId)}/price-search-prefill`,
  'POST',
  { expected_draft_revision: expectedDraftRevision, mode },
));

export const getConstructionPriceSearchPrefillJob = (flowId: string, jobId: string): Promise<PriceSearchPrefillJob> =>
  unwrap(apiCall<PriceSearchPrefillJob>(`${root(flowId)}/price-search-prefill/${encodeURIComponent(jobId)}`, 'GET'));

export const cancelConstructionPriceSearchPrefillJob = (flowId: string, jobId: string): Promise<PriceSearchPrefillJob> =>
  unwrap(apiCall<PriceSearchPrefillJob>(`${root(flowId)}/price-search-prefill/${encodeURIComponent(jobId)}/cancel`, 'POST', {}));

export const getConstructionLaborPlanPreview = (flowId: string): Promise<ConstructionLaborPlanPreview> =>
  unwrap(apiCall<ConstructionLaborPlanPreview>(`${root(flowId)}/labor-plan-preview`, 'GET'));

export const startConstructionLaborPlanJob = (
  flowId: string, expectedDraftRevision: number, expectedDraftContentHash: string, useSavedWork: boolean,
): Promise<ConstructionLaborPlanJob> => unwrap(apiCall<ConstructionLaborPlanJob>(
  `${root(flowId)}/labor-plan-jobs`, 'POST', {
    expected_draft_revision: expectedDraftRevision,
    expected_draft_content_hash: expectedDraftContentHash,
    use_saved_work: useSavedWork,
  },
));

export const getConstructionLaborPlanJob = (flowId: string, jobId: string): Promise<ConstructionLaborPlanJob> =>
  unwrap(apiCall<ConstructionLaborPlanJob>(`${root(flowId)}/labor-plan-jobs/${encodeURIComponent(jobId)}`, 'GET'));

export const cancelConstructionLaborPlanJob = (flowId: string, jobId: string): Promise<ConstructionLaborPlanJob> =>
  unwrap(apiCall<ConstructionLaborPlanJob>(`${root(flowId)}/labor-plan-jobs/${encodeURIComponent(jobId)}/cancel`, 'POST', {}));

export const applyConstructionLaborPlan = (
  flowId: string, proposalId: string, expectedDraftRevision: number, expectedDraftContentHash: string,
  siteStaffingRoles: SiteStaffingRoleSelection[],
  reviewedSchedule?: ReviewedLaborSchedule,
): Promise<{ draft: EstimateDraft; proposal: ConstructionLaborPlanProposal }> => unwrap(apiCall<{ draft: EstimateDraft; proposal: ConstructionLaborPlanProposal }>(
  `${root(flowId)}/labor-plan-proposals/${encodeURIComponent(proposalId)}/apply`, 'POST', {
    expected_draft_revision: expectedDraftRevision,
    expected_draft_content_hash: expectedDraftContentHash,
    site_staffing_roles: siteStaffingRoles,
    ...(reviewedSchedule ? { reviewed_schedule: reviewedSchedule } : {}),
  },
));

export const confirmConstructionPriceSearchLocation = (
  flowId: string,
  prefillRevision: number,
  expectedDraftRevision: number,
  projectLocation: EstimateDraft['project_location'],
): Promise<EstimateDraft> => unwrap(apiCall<EstimateDraft>(
  `${root(flowId)}/price-search-prefill/${encodeURIComponent(String(prefillRevision))}/location-confirmation`,
  'POST',
  {
    expected_draft_revision: expectedDraftRevision,
    project_location: normalizeProjectLocation(projectLocation),
  },
));

export const calculateConstructionEstimate = async (flowId: string, expectedDraftRevision: number, forceRefresh = false): Promise<EstimateJob> => {
  const value = await unwrap<EstimateJob>(apiCall<EstimateJob>(`${root(flowId)}/calculations`, 'POST', {
    expected_draft_revision: expectedDraftRevision,
    ...(forceRefresh ? { force_refresh: true } : {}),
  }));
  return normalizeJob(value)!;
};

/** Starts a server-composed schedule lookup; the browser cannot select a WD or source. */
export const startConstructionDavisBaconScheduleLookup = async (
  flowId: string,
  expectedDraftRevision: number,
  expectedDraftContentHash: string,
  useSavedWork: boolean,
): Promise<DavisBaconScheduleJob> => unwrap(apiCall<DavisBaconScheduleJob>(
  `${root(flowId)}/davis-bacon-schedule/lookup`,
  'POST',
  {
    expected_draft_revision: expectedDraftRevision,
    expected_draft_content_hash: expectedDraftContentHash,
    use_saved_work: useSavedWork,
  },
)).then((value) => normalizeDavisBaconScheduleJob(value)!);

/** Refresh is explicit: a server-owned new bounded lookup, not a browser source search. */
export const refreshConstructionDavisBaconSchedule = async (
  flowId: string,
  expectedDraftRevision: number,
  expectedDraftContentHash: string,
): Promise<DavisBaconScheduleJob> => unwrap(apiCall<DavisBaconScheduleJob>(
  `${root(flowId)}/davis-bacon-schedule/refresh`,
  'POST',
  {
    expected_draft_revision: expectedDraftRevision,
    expected_draft_content_hash: expectedDraftContentHash,
    use_saved_work: false,
  },
)).then((value) => normalizeDavisBaconScheduleJob(value)!);

export const getConstructionDavisBaconScheduleJob = (flowId: string, jobId: string): Promise<DavisBaconScheduleJob> =>
  unwrap(apiCall<DavisBaconScheduleJob>(`${root(flowId)}/davis-bacon-schedule/jobs/${encodeURIComponent(jobId)}`, 'GET'))
    .then((value) => normalizeDavisBaconScheduleJob(value)!);

export const cancelConstructionDavisBaconScheduleJob = (flowId: string, jobId: string): Promise<DavisBaconScheduleJob> =>
  unwrap(apiCall<DavisBaconScheduleJob>(`${root(flowId)}/davis-bacon-schedule/jobs/${encodeURIComponent(jobId)}/cancel`, 'POST', {}))
    .then((value) => normalizeDavisBaconScheduleJob(value)!);

/** Owner-only, policy-approved wage-row projection. No source artifact or location internals are accepted. */
export const getConstructionDavisBaconScheduleDetails = (flowId: string): Promise<DavisBaconScheduleDetails> =>
  unwrap(apiCall<DavisBaconScheduleDetails>(`${root(flowId)}/davis-bacon-schedule/details`, 'GET'))
    .then((value) => {
      const normalized = normalizeDavisBaconScheduleDetails(value);
      if (!normalized) throw new Error(USER_FACING_ERROR_MESSAGES.unrecognized);
      return normalized;
    });

/** Owner-only immutable review action bound to the server-issued schedule/token pair. */
export const reviewConstructionDavisBaconSchedule = (
  flowId: string,
  scheduleId: string,
  expectedReviewToken: string,
  decision: DavisBaconScheduleReviewDecision,
  reason: string,
): Promise<DavisBaconScheduleSummary> => unwrap(apiCall<DavisBaconScheduleSummary>(
  `${root(flowId)}/davis-bacon-schedule/review`, 'POST',
  { schedule_id: scheduleId, expected_review_token: expectedReviewToken, decision, reason },
)).then((value) => {
  const normalized = normalizeDavisBaconScheduleSummary(value);
  if (!normalized) throw new Error(USER_FACING_ERROR_MESSAGES.unrecognized);
  return normalized;
});

/**
 * Starts the server-owned primary-cost orchestration.  The browser supplies
 * only its accepted draft fence; it cannot select a source, snapshot,
 * geography, unit conversion, or pricing policy.
 */
export const calculateConstructionCostDataEstimate = async (
  flowId: string,
  expectedDraftRevision: number,
  expectedDraftContentHash: string,
  forceRefresh = false,
): Promise<EstimateJob> => {
  const value = await unwrap<EstimateJob>(apiCall<EstimateJob>(`${root(flowId)}/calculations`, 'POST', {
    expected_draft_revision: expectedDraftRevision,
    expected_draft_content_hash: expectedDraftContentHash,
    force_refresh: forceRefresh,
  }));
  return normalizeJob(value)!;
};

/** Query the server-derived public historical-award basis without pricing it. */
export const startConstructionAwardResearch = async (
  flowId: string,
  expectedDraftRevision: number,
  forceRefresh = false,
): Promise<EstimateJob> => {
  const value = await unwrap<EstimateJob>(apiCall<EstimateJob>(`${root(flowId)}/research`, 'POST', {
    expected_draft_revision: expectedDraftRevision,
    ...(forceRefresh ? { force_refresh: true } : {}),
  }));
  return normalizeJob(value)!;
};

/** Save-barrier-bound source search used by Stage 623's single pricing action. */
export const calculateConstructionPrices = async (
  flowId: string,
  expectedDraftRevision: number,
  expectedDraftContentHash: string,
  forceRefresh = false,
  aiPlanningEstimatesAcknowledged = false,
): Promise<EstimateJob> => {
  const value = await unwrap<EstimateJob>(apiCall<EstimateJob>(`${root(flowId)}/research`, 'POST', {
    expected_draft_revision: expectedDraftRevision,
    expected_draft_content_hash: expectedDraftContentHash,
    force_refresh: forceRefresh,
    ...(aiPlanningEstimatesAcknowledged ? { ai_planning_estimates_acknowledged: true } : {}),
  }));
  return normalizeJob(value)!;
};

/**
 * Ask the server to propose source matches after Contract Details has been
 * saved. The browser provides only the accepted-draft fence; it never sends
 * source taxonomy, prompt context, prices, or match facts.
 */
export const proposeConstructionSourceMatches = async (
  flowId: string,
  expectedDraftRevision: number,
  expectedDraftContentHash: string,
): Promise<SourceMatchProposalPublic | null> => {
  const value = await unwrap<unknown>(apiCall<unknown>(`${root(flowId)}/source-match-proposals`, 'POST', {
    expected_draft_revision: expectedDraftRevision,
    expected_draft_content_hash: expectedDraftContentHash,
  }));
  return normalizeSourceMatchProposalPublic(value);
};

/**
 * Owner review sends only a server-issued proposal revision and opaque
 * accept/decline decisions. The browser never submits a source, taxonomy key,
 * qualifier, location mapping, query, or price.
 */
export const reviewConstructionSourceMatches = async (
  flowId: string,
  proposalRevision: number,
  decisions: SourceMatchReviewDecision[],
): Promise<EstimateDraft | EstimateLoadData> => unwrap(apiCall<EstimateDraft | EstimateLoadData>(
  `${root(flowId)}/source-match-reviews`,
  'POST',
  { proposal_revision: proposalRevision, decisions: decisions.map(({ proposal_id, decision }) => ({ proposal_id, decision })) },
));

export const getConstructionEstimateJob = async (flowId: string, jobId: string): Promise<EstimateJob> => {
  const value = await unwrap<EstimateJob>(apiCall<EstimateJob>(`${root(flowId)}/calculations/${encodeURIComponent(jobId)}`, 'GET'));
  return normalizeJob(value)!;
};

export const cancelConstructionEstimateJob = async (flowId: string, jobId: string, reason?: string): Promise<EstimateJob> => {
  const value = await unwrap<EstimateJob>(apiCall<EstimateJob>(
    `${root(flowId)}/calculations/${encodeURIComponent(jobId)}/cancel`,
    'POST',
    reason ? { reason } : undefined,
  ));
  return normalizeJob(value)!;
};

export const getConstructionEstimateRevision = async (flowId: string, revision: number): Promise<EstimateRevision> => {
  const value = await unwrap<EstimateRevision>(apiCall<EstimateRevision>(`${root(flowId)}/revisions/${revision}`, 'GET'));
  return normalizeRevision(value)!;
};

export const compareConstructionEstimateRevisions = (flowId: string, revision: number, otherRevision: number): Promise<EstimateRevisionComparison> =>
  unwrap(apiCall<EstimateRevisionComparison>(`${root(flowId)}/revisions/${revision}/compare?other_revision=${otherRevision}`, 'GET'));

export const finalizeConstructionEstimate = (
  flowId: string,
  revision: number,
  expectedDraftRevision: number,
  acknowledgements: string[],
): Promise<EstimateRevision> => unwrap(apiCall<EstimateRevision>(
  `${root(flowId)}/revisions/${revision}/finalize`,
  'POST',
  { expected_draft_revision: expectedDraftRevision, acknowledgements },
)).then((value) => normalizeRevision(value)!);

export async function exportConstructionEstimate(flowId: string, revision: number): Promise<{ blob: Blob; filename: string }> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}${root(flowId)}/revisions/${revision}/export`, { credentials: 'include' });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const normalized = normalizeApiError(payload);
    const error = new Error(normalized.message) as CategorizedError;
    error.error_code = normalized.errorCode;
    error.technical_detail = normalized.technicalDetail;
    throw error;
  }
  const blob = await response.blob();
  if (blob.size <= 0) throw new Error('The estimate export was empty. Please try again.');
  const disposition = response.headers.get('content-disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const quoted = /filename="([^"]+)"/i.exec(disposition)?.[1];
  const filename = encoded ? decodeURIComponent(encoded) : quoted ?? `construction-estimate-revision-${revision}.pdf`;
  return { blob, filename };
}

export async function exportConstructionEstimateDraft(
  flowId: string,
  expectedDraftRevision: number,
  expectedDraftContentHash: string,
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}${root(flowId)}/draft/export`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expected_draft_revision: expectedDraftRevision, expected_draft_content_hash: expectedDraftContentHash }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const normalized = normalizeApiError(payload);
    const error = new Error(normalized.message) as CategorizedError;
    error.error_code = normalized.errorCode;
    error.technical_detail = normalized.technicalDetail;
    throw error;
  }
  const blob = await response.blob();
  if (blob.size <= 0) throw new Error('The draft estimate export was empty. Please try again.');
  return { blob, filename: 'construction-estimate-draft.pdf' };
}
